import React, { useState, useEffect, memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection, getDocs, query, orderBy, where,
  doc, getDoc, updateDoc, addDoc, serverTimestamp, Timestamp, onSnapshot, increment
} from 'firebase/firestore';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import VendorCard from '../components/VendorCard';
import { showError, showSuccess } from '../utils/notifications';
import { useStableCallback } from '../hooks/useOptimizedCallback';
import { logStatusChange } from '../services/auditService';

const RecargasSD = memo(() => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [activeSection, setActiveSection] = useState('recargas-sd');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' o 'table'
  const [sortMode, setSortMode] = useState('tickets'); // 'tickets', 'name', 'status'
  const [searchTerm, setSearchTerm] = useState(''); // Estado para el término de búsqueda

  // Estados para tickets disponibles en el sistema
  const [availableTickets, setAvailableTickets] = useState({
    cantidadAuto: 0,
    cantidadMoto: 0,
    tokenAuto: '',
    tokenMoto: '',
    fechaEcuador: '',
    ultimaActualizacion: ''
  });

  // Estados para la paginación
  const [currentPage, setCurrentPage] = useState(1);
  const vendorsPerPage = 15; // 15 vendors por página
    // Usar useMemo para obtener la lista de vendedores filtrada y ordenada
  const sortedVendors = useMemo(() => {
    // Primero filtrar por término de búsqueda
    let vendorsList = [...vendors];

    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase().trim();
      vendorsList = vendorsList.filter(vendor =>
        // Buscar por nombre
        (vendor.name && vendor.name.toLowerCase().includes(searchLower)) ||
        // Buscar por número de CA
        (vendor.numberCa && vendor.numberCa.toLowerCase().includes(searchLower)) ||
        // Buscar por cédula
        (vendor.identificationNumber && vendor.identificationNumber.toLowerCase().includes(searchLower))
      );
    }

    // Luego ordenar según el criterio seleccionado
    switch (sortMode) {
      case 'tickets':
        vendorsList.sort((a, b) => {
          const totalTicketsA = (a.ticketsMoto || 0) + (a.ticketsAuto || 0);
          const totalTicketsB = (b.ticketsMoto || 0) + (b.ticketsAuto || 0);

          // Primero por total de tickets (ascendente - menos tickets primero)
          if (totalTicketsA !== totalTicketsB) {
            return totalTicketsA - totalTicketsB;
          }

          // Si tienen los mismos tickets totales, ordenar por el mínimo entre moto y auto
          const minTicketsA = Math.min(a.ticketsMoto || 0, a.ticketsAuto || 0);
          const minTicketsB = Math.min(b.ticketsMoto || 0, b.ticketsAuto || 0);

          if (minTicketsA !== minTicketsB) {
            return minTicketsA - minTicketsB;
          }

          // Si todo es igual, ordenar alfabéticamente
          return (a.name || '').localeCompare(b.name || '');
        });
        break;

      case 'name':
        vendorsList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;

      case 'status':
        vendorsList.sort((a, b) => {
          // Primero activos, luego inactivos
          if (a.status !== b.status) {
            return a.status === 'active' ? -1 : 1;
          }
          return (a.name || '').localeCompare(b.name || '');
        });
        break;

      default:
        break;
    }
    return vendorsList;
  }, [vendors, sortMode, searchTerm]);

  // Calculamos los índices para la paginación
  const indexOfLastVendor = currentPage * vendorsPerPage;
  const indexOfFirstVendor = indexOfLastVendor - vendorsPerPage;
  const currentVendors = sortedVendors.slice(indexOfFirstVendor, indexOfLastVendor);
  const totalPages = Math.ceil(sortedVendors.length / vendorsPerPage);

  // Funciones para la navegación de paginación
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const navigate = useNavigate();

  const validateRechargeWithAPI = useStableCallback(async (rechargeData) => {
    console.log(`🔒 [SD] Iniciando validación con API SERT...`);

    try {
      const response = await fetch('/api/validate-recharge-sd', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rechargeData),
        signal: AbortSignal.timeout(25000)
      });

      const result = await response.json();
      console.log(`📡 [SD] Respuesta SERT (${response.status}):`, result);

      if (!response.ok || !result.success) {
        console.error(`❌ [SD] Validación SERT rechazada:`, result.message);
        return {
          success: false,
          message: result.message || `Error ${response.status}: Validación rechazada por SERT`
        };
      }

      console.log(`✅ [SD] Validación SERT aprobada`);
      return { success: true, message: result.message, data: result.data };

    } catch (error) {
      console.error(`❌ [SD] Error conectando con SERT:`, error);
      const msg = error.name === 'AbortError'
        ? 'Tiempo de espera agotado (25s) al conectar con SERT'
        : `Error de conexión con SERT: ${error.message}`;
      return { success: false, message: msg };
    }
  });

  // Fetch vendors with stable callback
  const fetchVendors = useStableCallback(async () => {
    try {
      const usuariosRef = collection(db, 'usuarios');
      const q = query(usuariosRef, where("rol", "==", "CA"), orderBy('nombre'));
      const querySnapshot = await getDocs(q);

      const vendorsList = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        const isSpecialCA = doc.id === 'sangyingch@gmail.com' ||
                           (data.email === 'sangyingch@gmail.com');

        if (isSpecialCA) {
          console.log(`⚠️ CA especial detectado: ${doc.id}`);
        }

        let formattedTimestamp = 'No disponible';
        if (data.ubicacionHoraFecha) {
          try {
            if (data.ubicacionHoraFecha instanceof Timestamp) {
              const date = data.ubicacionHoraFecha.toDate();
              formattedTimestamp = date.toLocaleString('es-ES');
            } else if (typeof data.ubicacionHoraFecha === 'string') {
              formattedTimestamp = data.ubicacionHoraFecha;
            }
          } catch (error) {
            console.log("Error formateando timestamp:", error);
          }
        }

        const vendorId = doc.id;

        vendorsList.push({
          id: vendorId,
          userId: vendorId,
          documentId: vendorId,
          ...data,
          name: data.nombre,
          numberCa: data.numberCa || 'N/A',
          balance: data.balance || 0,
          status: data.status === 1 ? 'active' : 'inactive',
          ticketsMoto: data.ticketsMoto || 0,
          ticketsAuto: data.ticketsAuto || 0,
          identificationNumber: data.cedula || 'N/A',
          telefono: data.telefono || 'N/A',
          zone: data.zona || 'N/A',
          email: data.email || vendorId,
          ticketsSold: data.ticketsSold || 0,
          revenueGenerated: data.revenueGenerated || 0,
          latitud: data.latitud || null,
          longitud: data.longitud || null,
          ubicacionHoraFecha: formattedTimestamp,
          isSpecialCA: isSpecialCA
        });
      });

      setVendors(vendorsList);
    } catch (error) {
      console.error("Error loading vendors:", error);
      showError("Error", "No se pudieron cargar los Controladores Autorizados");
    }
  });

  // Handle vendor status toggle
  const handleStatusToggle = useStableCallback(async (vendorId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 0 : 1;
      const vendorRef = doc(db, 'usuarios', vendorId);

      const vendor = vendors.find(v => v.id === vendorId);
      const vendorName = vendor?.name || vendor?.nombre || 'Unknown';
      const vendorNumber = vendor?.numberCa || 'N/A';

      await updateDoc(vendorRef, {
        status: newStatus,
        lastModified: serverTimestamp()
      });

      await logStatusChange({
        entityId: vendorId,
        entityType: 'vendor',
        entityName: vendorName,
        entityNumber: vendorNumber,
        previousStatus: currentStatus,
        newStatus: newStatus === 1 ? 'active' : 'inactive',
        changedBy: user?.email || 'Unknown',
        additionalDetails: {
          operationType: 'vendor_status_toggle',
          source: 'recargas_sd_page',
          vendorRole: 'CA'
        }
      });

      setVendors(prev => prev.map(vendor =>
        vendor.id === vendorId
          ? { ...vendor, status: newStatus === 1 ? 'active' : 'inactive' }
          : vendor
      ));

      return {
        success: true,
        newStatus: newStatus === 1 ? 'active' : 'inactive'
      };

    } catch (error) {
      console.error("Error updating vendor status:", error);
      throw new Error("No se pudo actualizar el estado del vendedor");
    }
  });

  // Handle vendor edit
  const handleVendorEdit = useStableCallback(async (vendorId, editData) => {
    try {
      const vendorRef = doc(db, 'usuarios', vendorId);

      await updateDoc(vendorRef, {
        nombre: editData.nombre,
        cedula: editData.cedula,
        telefono: editData.telefono,
        zona: editData.zona,
        lastModified: serverTimestamp()
      });

      setVendors(prev => prev.map(vendor =>
        vendor.id === vendorId
          ? {
              ...vendor,
              name: editData.nombre,
              nombre: editData.nombre,
              identificationNumber: editData.cedula,
              cedula: editData.cedula,
              telefono: editData.telefono,
              zone: editData.zona,
              zona: editData.zona
            }
          : vendor
      ));

      return {
        success: true,
        userName: editData.nombre
      };

    } catch (error) {
      console.error("Error editing vendor:", error);
      throw new Error("No se pudieron actualizar los datos del vendedor");
    }
  });

  // FUNCIÓN PRINCIPAL DE RECARGA - Directa en Firestore
  const handleVendorRecharge = useStableCallback(async (vendorId, rechargeData) => {
    try {
      console.log(`🔄 Iniciando recarga directa Firestore para CA: ${vendorId}`);

      const normalizedVehicleType = rechargeData?.vehicleType === 'moto' ? 'moto' : rechargeData?.vehicleType === 'auto' ? 'auto' : null;
      if (!normalizedVehicleType) {
        throw new Error("Tipo de vehículo inválido. Debe ser 'moto' o 'auto'.");
      }

      const ticketsToAdd = Number(rechargeData?.tickets);
      if (!Number.isFinite(ticketsToAdd) || ticketsToAdd <= 0) {
        throw new Error("Cantidad de tickets inválida para recarga.");
      }

      const vendor = vendors.find(v => v.id === vendorId);
      if (!vendor) {
        throw new Error("Vendedor no encontrado");
      }

      const vendorRef = doc(db, 'usuarios', vendorId);
      const vendorSnapshot = await getDoc(vendorRef);
      if (!vendorSnapshot.exists()) {
        throw new Error("No se encontró el documento del CA en Firestore.");
      }
      const vendorData = vendorSnapshot.data();

      let clientIP = 'No disponible';
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json', { signal: AbortSignal.timeout(2000) });
        if (ipRes.ok) {
          const data = await ipRes.json();
          clientIP = data.ip;
        }
      } catch (ipError) {
        console.log("No se pudo obtener IP externa:", ipError.message);
        clientIP = 'Panel Admin';
      }

      const requestsRef = collection(db, 'tokensd');
      const activeRequestsQuery = query(requestsRef, where('en_uso', '==', true));
      const requestsSnapshot = await getDocs(activeRequestsQuery);

      if (requestsSnapshot.empty) {
        throw new Error("No hay token SD activo en la colección tokensd (en_uso=true).");
      }

      let selectedRequestId = '';
      let selectedRequestData = null;
      let maxAvailableForType = -1;

      requestsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const cantidadAuto = Number(data.cantidadAuto) || 0;
        const cantidadMoto = Number(data.cantidadMoto) || 0;
        const consumidosAuto = Number(data.ticketsConsumidosAuto) || 0;
        const consumidosMoto = Number(data.ticketsConsumidosMoto) || 0;
        const disponibleAuto = Math.max(0, cantidadAuto - consumidosAuto);
        const disponibleMoto = Math.max(0, cantidadMoto - consumidosMoto);
        const availableForType = normalizedVehicleType === 'moto' ? disponibleMoto : disponibleAuto;

        if (availableForType > maxAvailableForType) {
          maxAvailableForType = availableForType;
          selectedRequestId = docSnap.id;
          selectedRequestData = data;
        }
      });

      if (!selectedRequestData) {
        throw new Error("No se pudo seleccionar una solicitud activa para descontar tickets.");
      }

      if (maxAvailableForType < ticketsToAdd) {
        throw new Error(`No hay tickets suficientes de ${normalizedVehicleType}. Disponibles: ${maxAvailableForType}, solicitados: ${ticketsToAdd}.`);
      }

      const tokenToUse = normalizedVehicleType === 'moto'
        ? (selectedRequestData.tokenMoto || selectedRequestData.tokenAuto || '')
        : (selectedRequestData.tokenAuto || selectedRequestData.tokenMoto || '');
      if (!tokenToUse) {
        throw new Error("La solicitud activa no tiene token configurado.");
      }

      const currentDate = new Date();
      const fechaStr = currentDate.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZoneName: 'longOffset'
      });

      // ── VALIDACIÓN API SERT ───────────────────────────────────────────────
      // Debe aprobarse antes de tocar Firestore
      const rechargeDocId = `RSD${Date.now()}`;
      const validationPayload = {
        documentId:    rechargeDocId,
        adminEmail:    user?.email || '',
        createdAt:     fechaStr,
        description:   `Recarga de ${ticketsToAdd} tickets para ${normalizedVehicleType === 'moto' ? 'motos' : 'autos'}`,
        ipAddress:     clientIP,
        numberCa:      vendor.numberCa || 'N/A',
        operationType: 'recarga_tickets',
        vehicleType:   normalizedVehicleType,
        status:        'completed',
        platform:      'web_admin',
        price:         String(rechargeData.price ?? 0),
        ticketsAdded:  String(ticketsToAdd),
        userName:      vendor.name || vendor.nombre || '',
        vendorId:      vendorId,
        tokenticked:   tokenToUse
      };

      const validationResult = await validateRechargeWithAPI(validationPayload);
      if (!validationResult.success) {
        throw new Error(`Recarga no autorizada por SERT: ${validationResult.message}`);
      }
      console.log(`✅ [SD] SERT aprobó la recarga. Procediendo con registro en Firestore...`);
      // ─────────────────────────────────────────────────────────────────────

      const previousTicketsMoto = Number(vendorData.ticketsMoto) || 0;
      const previousTicketsAuto = Number(vendorData.ticketsAuto) || 0;
      const newTicketsMoto = normalizedVehicleType === 'moto' ? previousTicketsMoto + ticketsToAdd : previousTicketsMoto;
      const newTicketsAuto = normalizedVehicleType === 'auto' ? previousTicketsAuto + ticketsToAdd : previousTicketsAuto;

      const rechargeRecord = {
        adminEmail: user?.email || 'Sistema',
        amount: rechargeData.price ?? 0,
        cedula: vendor.identificationNumber || vendor.cedula || 'N/A',
        description: `Recarga de ${rechargeData.tickets} tickets para ${normalizedVehicleType === 'moto' ? 'motos' : 'autos'}`,
        operationType: 'recarga_tickets',
        tokenticked: tokenToUse,
        adminId: user?.uid || 'unknown',
        adminName: user?.nombre || user?.displayName || user?.email || 'Sistema',
        clientIP,
        createdAt: serverTimestamp(),
        createdBy: user?.email || 'Sistema',
        date: serverTimestamp(),
        direccion: vendor.direccion || 'N/A',
        executedBy: user?.nombre || user?.displayName || user?.email || 'Sistema',
        executorName: user?.nombre || user?.displayName || user?.email || 'Sistema',
        fechaDocumento: fechaStr,
        fechaOperacion: serverTimestamp(),
        ip: clientIP,
        ipAddress: clientIP,
        newTicketsMoto,
        newTicketsAuto,
        numberCa: vendor.numberCa || 'N/A',
        paymentMethod: rechargeData.paymentMethod || 'efectivo',
        platform: 'web_admin',
        previousTicketsMoto,
        previousTicketsAuto,
        price: rechargeData.price ?? 0,
        status: 'completed',
        telefono: vendor.telefono || 'N/A',
        ticketsAdded: rechargeData.tickets,
        timestamp: serverTimestamp(),
        userName: vendor.name || vendor.nombre,
        vehicleType: rechargeData.vehicleType,
        vendorId
      };

      await addDoc(collection(db, 'Recharge'), rechargeRecord);

      // Actualizar resumen de stock SD
      if (normalizedVehicleType === 'moto') {
        await updateDoc(doc(db, 'resumen', 'stockDisponibles'), {
          soldMoto: increment(ticketsToAdd),
          availableMoto: increment(-ticketsToAdd),
          updatedAt: serverTimestamp(),
        });
      } else {
        await updateDoc(doc(db, 'resumen', 'stockDisponibles'), {
          soldAuto: increment(ticketsToAdd),
          availableAuto: increment(-ticketsToAdd),
          updatedAt: serverTimestamp(),
        });
      }

      const vendorUpdateData = {
        lastModified: serverTimestamp(),
        lastRecharge: {
          amount: rechargeData.price ?? 0,
          date: serverTimestamp(),
          tickets: ticketsToAdd,
          vehicleType: normalizedVehicleType,
          paymentMethod: rechargeData.paymentMethod || 'efectivo'
        }
      };
      if (normalizedVehicleType === 'moto') {
        vendorUpdateData.ticketsMoto = newTicketsMoto;
      } else {
        vendorUpdateData.ticketsAuto = newTicketsAuto;
      }
      await updateDoc(vendorRef, vendorUpdateData);

      const consumedField = normalizedVehicleType === 'moto' ? 'ticketsConsumidosMoto' : 'ticketsConsumidosAuto';
      const currentConsumed = Number(selectedRequestData[consumedField]) || 0;
      await updateDoc(doc(db, 'tokensd', selectedRequestId), {
        [consumedField]: currentConsumed + ticketsToAdd,
        ultimaActualizacion: serverTimestamp()
      });

      setVendors(prev => prev.map(v =>
        v.id === vendorId
          ? { ...v, ticketsMoto: newTicketsMoto, ticketsAuto: newTicketsAuto }
          : v
      ));

      showSuccess("Recarga procesada", `Se recargaron ${rechargeData.tickets} tickets (${rechargeData.vehicleType}) al CA ${vendor.name || vendor.nombre}.`);
    } catch (error) {
      console.error("Error en proceso de recarga:", error);
      const msg = error.message || "No se pudo procesar la recarga de tickets";
      throw new Error(msg);
    }
  });

  // Setup real-time vendors
  const setupRealTimeVendors = useStableCallback(async () => {
    await fetchVendors();
  });

  useEffect(() => {
    setupRealTimeVendors();
  }, [setupRealTimeVendors]);

  // useEffect: info del token desde tokensd + balance por NODE_ENV
  useEffect(() => {
    if (!user || (userRole !== 'Admin' && userRole !== 'Supervisor')) return;

    let tokenInfo = { tokenMoto: '', tokenAuto: '', fechaEcuador: '', ultimaActualizacion: '', documentId: '' };
    let balanceMoto = 0;
    let balanceAuto = 0;

    const applyState = () => {
      setAvailableTickets({ cantidadMoto: balanceMoto, cantidadAuto: balanceAuto, ...tokenInfo });
    };

    // Siempre leer tokensd para seleccionar el token de la recarga
    const q = query(collection(db, 'tokensd'), where('en_uso', '==', true));
    const unsubToken = onSnapshot(q, (snap) => {
      let bestDoc = null, bestDocId = '', maxTickets = 0;
      snap.forEach((d) => {
        const data = d.data();
        const total = (Number(data.cantidadMoto) || 0) + (Number(data.cantidadAuto) || 0);
        if (total > maxTickets) { maxTickets = total; bestDoc = data; bestDocId = d.id; }
      });
      if (bestDoc) {
        tokenInfo = {
          tokenMoto: bestDoc.tokenMoto || '',
          tokenAuto: bestDoc.tokenAuto || '',
          fechaEcuador: bestDoc.fechaEcuador || '',
          ultimaActualizacion: bestDoc.ultimaActualizacion || '',
          documentId: bestDocId,
        };
      }
      applyState();
    }, (error) => {
      console.error('❌ Error listener tokensd:', error);
      showError('Error', 'No se pudo conectar a los datos de tickets disponibles');
    });

    // Balance: en desarrollo desde colecciones directas; en producción desde resumen
    let unsubBalance;
    if (process.env.NODE_ENV === 'development') {
      let purchasedMoto = 0, purchasedAuto = 0, soldMoto = 0, soldAuto = 0;
      let histReady = false, rechReady = false;

      const recalc = () => {
        if (!histReady || !rechReady) return;
        balanceMoto = Math.max(0, purchasedMoto - soldMoto);
        balanceAuto = Math.max(0, purchasedAuto - soldAuto);
        applyState();
      };

      const unsubHist = onSnapshot(collection(db, 'ticketRequestsHistory'), (snap) => {
        purchasedMoto = 0; purchasedAuto = 0;
        snap.forEach((d) => {
          purchasedMoto += parseInt(d.data().ticketsMotos) || 0;
          purchasedAuto += parseInt(d.data().ticketsVehiculos) || 0;
        });
        histReady = true; recalc();
      });

      const unsubRech = onSnapshot(collection(db, 'Recharge'), (snap) => {
        soldMoto = 0; soldAuto = 0;
        snap.forEach((d) => {
          const data = d.data();
          const t = parseInt(data.ticketsAdded) || 0;
          if (data.vehicleType === 'moto') soldMoto += t; else soldAuto += t;
        });
        rechReady = true; recalc();
      });

      unsubBalance = () => { unsubHist(); unsubRech(); };
    } else {
      const unsubResumen = onSnapshot(doc(db, 'resumen', 'stockDisponibles'), (snap) => {
        if (snap.exists()) {
          const d = snap.data();
          balanceMoto = Math.max(0, d.availableMoto ?? 0);
          balanceAuto = Math.max(0, d.availableAuto ?? 0);
          applyState();
        }
      }, (error) => console.error('❌ Error listener resumen/stockDisponibles:', error));
      unsubBalance = unsubResumen;
    }

    return () => { unsubToken(); unsubBalance(); };
  }, [user, userRole]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          let userData = null;

          try {
            const userDocRef = doc(db, 'usuarios', currentUser.email);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              userData = userDoc.data();
            }
          } catch (error) {
            console.log("Lookup by email failed:", error);
          }

          if (!userData) {
            try {
              const userDocRefByUID = doc(db, 'usuarios', currentUser.uid);
              const userDoc = await getDoc(userDocRefByUID);
              if (userDoc.exists()) {
                userData = userDoc.data();
              }
            } catch (error) {
              console.log("Lookup by UID failed:", error);
            }
          }

          if (!userData) {
            try {
              const usuariosRef = collection(db, 'usuarios');
              const q = query(usuariosRef, where('email', '==', currentUser.email));
              const querySnapshot = await getDocs(q);

              if (!querySnapshot.empty) {
                querySnapshot.forEach((doc) => {
                  userData = doc.data();
                });
              }
            } catch (error) {
              console.log("Email field query failed:", error);
            }
          }

          if (!userData) {
            showError("Usuario no encontrado", "Tu perfil no existe en el sistema");
            auth.signOut();
            navigate('/login');
            return;
          }

          setUser({
            ...currentUser,
            rol: userData.rol,
            nombre: userData.nombre
          });

          setUserRole(userData.rol);

          if (userData.rol === 'Admin' || userData.rol === 'Supervisor') {
            await fetchVendors();
          } else {
            showError("Acceso denegado", "No tienes permisos para acceder a esta página");
            navigate('/dashboard');
          }
        } catch (error) {
          console.error("Error verifying user:", error);
          showError("Error de autenticación", "Hubo un problema al verificar tus credenciales");
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, fetchVendors]);

  // Función para manejar la navegación a detalles de CA
  const handleNavigateToDetails = (vendor) => {
    if (vendor.isSpecialCA || vendor.id === 'sangyingch@gmail.com' || vendor.email === 'sangyingch@gmail.com') {
      console.log(`🔍 Navegación especial para CA: ${vendor.name || vendor.id}`);

      const idToUse = vendor.email || vendor.id;
      console.log(`ID especial a utilizar: ${idToUse}`);

      if (idToUse.includes('@')) {
        navigate(`/vendors/${idToUse}`);
      } else {
        showError('Error', 'No se pudo determinar el ID del controlador');
      }
    } else {
      console.log(`Navegando a detalles de CA estándar: ${vendor.id}`);
      navigate(`/vendors/${vendor.id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Mobile Header */}
      <MobileHeader
        title="Recargas SD"
        userEmail={user?.email}
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      {/* Add top spacing for mobile */}
      <div className="md:hidden h-14"></div>

      <div className="flex flex-1 overflow-hidden">
        {/* Overlay for mobile menu */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}

        {/* Sidebar */}
        <Sidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {/* Top Header (desktop only) */}
          <header className="bg-white shadow px-6 py-4 hidden md:block">
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-bold text-gray-900">Recargas SD</h1>
              <div className="text-right flex items-center">
                <div className="mr-4">
                  <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                  <p className="text-xs text-gray-500">{userRole || "Usuario"}</p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      localStorage.removeItem('userRole');
                      await auth.signOut();
                      navigate('/login');
                    } catch (error) {
                      console.error("Error signing out:", error);
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1 rounded"
                >
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="p-6">
            {/* Tickets Disponibles en el Sistema - OCULTO */}
            {false && (
            <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-blue-800 flex items-center">
                  🎫 Tickets Disponibles en el Sistema
                </h3>
                <div className="text-xs text-blue-600">
                  {availableTickets.ultimaActualizacion && (
                    <>Última actualización: {availableTickets.ultimaActualizacion}</>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Tickets Motos */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">🏍️ Tickets Motos</p>
                      <p className="text-2xl font-bold text-blue-600">{availableTickets.cantidadMoto.toLocaleString()}</p>
                      {availableTickets.tokenMoto && (
                        <p className="text-xs text-gray-500 mt-1">Token: {availableTickets.tokenMoto}</p>
                      )}
                    </div>
                    <div className={`p-2 rounded-full ${availableTickets.cantidadMoto < 100 ? 'bg-red-100' : availableTickets.cantidadMoto < 500 ? 'bg-yellow-100' : 'bg-green-100'}`}>
                      <span className={`text-lg ${availableTickets.cantidadMoto < 100 ? 'text-red-600' : availableTickets.cantidadMoto < 500 ? 'text-yellow-600' : 'text-green-600'}`}>
                        🏍️
                      </span>
                    </div>
                  </div>
                </div>

                {/* Tickets Autos */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">🚗 Tickets Autos</p>
                      <p className="text-2xl font-bold text-green-600">{availableTickets.cantidadAuto.toLocaleString()}</p>
                      {availableTickets.tokenAuto && (
                        <p className="text-xs text-gray-500 mt-1">Token: {availableTickets.tokenAuto}</p>
                      )}
                    </div>
                    <div className={`p-2 rounded-full ${availableTickets.cantidadAuto < 100 ? 'bg-red-100' : availableTickets.cantidadAuto < 500 ? 'bg-yellow-100' : 'bg-green-100'}`}>
                      <span className={`text-lg ${availableTickets.cantidadAuto < 100 ? 'text-red-600' : availableTickets.cantidadAuto < 500 ? 'text-yellow-600' : 'text-green-600'}`}>
                        🚗
                      </span>
                    </div>
                  </div>
                </div>

                {/* Total Tickets */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">📊 Total Tickets</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {(availableTickets.cantidadMoto + availableTickets.cantidadAuto).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Motos + Autos
                      </p>
                    </div>
                    <div className="p-2 rounded-full bg-purple-100">
                      <span className="text-lg text-purple-600">📊</span>
                    </div>
                  </div>
                </div>

                {/* Estado del Sistema */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">⚡ Estado Sistema</p>
                      <p className={`text-lg font-bold ${
                        (availableTickets.cantidadMoto + availableTickets.cantidadAuto) > 1000
                          ? 'text-green-600'
                          : (availableTickets.cantidadMoto + availableTickets.cantidadAuto) > 200
                            ? 'text-yellow-600'
                            : 'text-red-600'
                      }`}>
                        {(availableTickets.cantidadMoto + availableTickets.cantidadAuto) > 1000
                          ? 'ÓPTIMO'
                          : (availableTickets.cantidadMoto + availableTickets.cantidadAuto) > 200
                            ? 'ADVERTENCIA'
                            : 'CRÍTICO'}
                      </p>
                      {availableTickets.fechaEcuador && (
                        <p className="text-xs text-gray-500 mt-1">
                          {availableTickets.fechaEcuador}
                        </p>
                      )}
                    </div>
                    <div className={`p-2 rounded-full ${
                      (availableTickets.cantidadMoto + availableTickets.cantidadAuto) > 1000
                        ? 'bg-green-100'
                        : (availableTickets.cantidadMoto + availableTickets.cantidadAuto) > 200
                          ? 'bg-yellow-100'
                          : 'bg-red-100'
                    }`}>
                      <span className={`text-lg ${
                        (availableTickets.cantidadMoto + availableTickets.cantidadAuto) > 1000
                          ? 'text-green-600'
                          : (availableTickets.cantidadMoto + availableTickets.cantidadAuto) > 200
                            ? 'text-yellow-600'
                            : 'text-red-600'
                      }`}>
                        {(availableTickets.cantidadMoto + availableTickets.cantidadAuto) > 1000
                          ? '✅'
                          : (availableTickets.cantidadMoto + availableTickets.cantidadAuto) > 200
                            ? '⚠️'
                            : '🚨'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Alerta de tickets insuficientes */}
              {(availableTickets.cantidadMoto + availableTickets.cantidadAuto) < 1000 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <span className="text-red-400 text-xl">🚨</span>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        ⚠️ RECARGAS TEMPORALMENTE DESHABILITADAS
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>
                          No es posible realizar recargas porque el sistema tiene menos de 1,000 tickets disponibles.
                          <br />
                          <strong>Tickets actuales:</strong> {(availableTickets.cantidadMoto + availableTickets.cantidadAuto).toLocaleString()}
                          <strong> | Requeridos:</strong> 1,000 mínimo
                          <br />
                          <strong>Faltan:</strong> {(1000 - (availableTickets.cantidadMoto + availableTickets.cantidadAuto)).toLocaleString()} tickets para habilitar recargas.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* View Mode Toggle and Sort Controls */}
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-700">
                  Lista de Controladores ({sortedVendors.length} de {vendors.length})
                </h2>
                <p className="text-sm text-gray-500">
                  Gestiona los Controladores Autorizados del sistema
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                {/* Search Field */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar por nombre, número CA o cédula"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      aria-label="Clear search"
                    >
                      <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Sort Controls */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-600">
                    Ordenar por:
                  </label>
                  <select
                    value={sortMode}
                    onChange={(e) => setSortMode(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="tickets">🎫 Menos Tickets (Urgente)</option>
                    <option value="name">📝 Nombre A-Z</option>
                    <option value="status">⚡ Estado (Activos Primero)</option>
                  </select>
                </div>

                {/* View Mode Toggle */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => setViewMode('cards')}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      viewMode === 'cards'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Vista Tarjetas
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      viewMode === 'table'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Vista Tabla
                  </button>
                </div>
              </div>
            </div>

            {/* Cards View */}
            {viewMode === 'cards' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentVendors.map((vendor) => (
                  <VendorCard
                    key={vendor.id}
                    vendor={vendor}
                    onRecharge={handleVendorRecharge}
                    onToggleStatus={handleStatusToggle}
                    onEdit={handleVendorEdit}
                    isAdmin={userRole === 'Admin'}
                    isSupervisor={userRole === 'Supervisor'}
                    lowBalance={vendor.ticketsMoto < 15 || vendor.ticketsAuto < 15}
                    availableTickets={availableTickets}
                    canRecharge={(availableTickets.cantidadMoto + availableTickets.cantidadAuto) >= 1000}
                  />
                ))}
                {vendors.length === 0 && (
                  <div className="col-span-full text-center py-10">
                    <p className="text-gray-500">No hay Controladores Autorizados disponibles.</p>
                  </div>
                )}
                {vendors.length > 0 && sortedVendors.length === 0 && (
                  <div className="col-span-full text-center py-10">
                    <p className="text-gray-500">No hay resultados para "{searchTerm}".</p>
                    <button
                      onClick={() => setSearchTerm('')}
                      className="mt-2 text-blue-500 hover:text-blue-700 font-medium"
                    >
                      Limpiar búsqueda
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nombre
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          CA ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cédula
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Zona
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tickets Motos
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tickets Autos
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Última Ubicación
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ubicación
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentVendors.map((vendor) => (
                        <tr
                          key={vendor.id}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {vendor.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {vendor.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {vendor.numberCa}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {vendor.identificationNumber || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {vendor.zone || 'No definida'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`font-bold ${vendor.ticketsMoto < 10 ? 'text-red-600' : 'text-blue-600'}`}>
                              {vendor.ticketsMoto}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`font-bold ${vendor.ticketsAuto < 10 ? 'text-red-600' : 'text-green-600'}`}>
                              {vendor.ticketsAuto}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              vendor.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {vendor.status === 'active' ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {vendor.ubicacionHoraFecha || 'No disponible'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {(vendor.latitud && vendor.longitud) ? (
                              <a
                                href={`https://www.google.com/maps?q=${vendor.latitud},${vendor.longitud}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 inline-flex items-center"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Ver Mapa
                              </a>
                            ) : (
                              <span className="text-gray-500">No disponible</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleNavigateToDetails(vendor)}
                              className="px-3 py-1 bg-indigo-500 text-white rounded hover:bg-indigo-600 inline-flex items-center"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              Ver Detalles
                            </button>
                          </td>
                        </tr>
                      ))}
                      {vendors.length === 0 && (
                        <tr>
                          <td colSpan="10" className="px-6 py-10 text-center text-gray-500">
                            <p>No hay Controladores Autorizados disponibles.</p>
                          </td>
                        </tr>
                      )}
                      {vendors.length > 0 && sortedVendors.length === 0 && (
                        <tr>
                          <td colSpan="10" className="px-6 py-10 text-center text-gray-500">
                            <p>No hay resultados para "{searchTerm}".</p>
                            <button
                              onClick={() => setSearchTerm('')}
                              className="mt-2 text-blue-500 hover:text-blue-700 font-medium"
                            >
                              Limpiar búsqueda
                            </button>
                          </td>
                        </tr>
                      )}
                      {vendors.length > 0 && sortedVendors.length > 0 && currentVendors.length === 0 && (
                        <tr>
                          <td colSpan="10" className="px-6 py-10 text-center text-gray-500">
                            <p>No hay controladores en esta página.</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pagination Controls */}
            {viewMode === 'table' && sortedVendors.length > vendorsPerPage && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Anterior
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{indexOfFirstVendor + 1}</span> a{' '}
                      <span className="font-medium">
                        {Math.min(indexOfLastVendor, sortedVendors.length)}
                      </span>{' '}
                      de <span className="font-medium">{sortedVendors.length}</span> controladores
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                      >
                        <span className="sr-only">Anterior</span>
                        &larr;
                      </button>

                      {Array.from({ length: Math.min(totalPages, 5) }).map((_, idx) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = idx + 1;
                        } else if (currentPage <= 3) {
                          if (idx < 4) {
                            pageNum = idx + 1;
                          } else {
                            pageNum = totalPages;
                          }
                        } else if (currentPage >= totalPages - 2) {
                          if (idx === 0) {
                            pageNum = 1;
                          } else {
                            pageNum = totalPages - (4 - idx);
                          }
                        } else {
                          pageNum = currentPage - 2 + idx;
                        }

                        const isEllipsis = (idx === 0 && pageNum !== 1 && pageNum !== currentPage - 2) ||
                                           (idx === 4 && pageNum !== totalPages && pageNum !== currentPage + 2);

                        return isEllipsis ? (
                          <span
                            key={`ellipsis-${idx}`}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                      >
                        <span className="sr-only">Siguiente</span>
                        &rarr;
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
});

export default RecargasSD;
